import React from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { formatCalendarDate } from "kontrolEdge/components/Shared/VmSpecsCard";
import type { LicensesSection, LicenseService } from "./overview.types";
import OverviewCard from "./OverviewCard";
import { EMPTY, serviceLabel } from "./overviewFormat";

type TagVariant = "success" | "warning" | "error" | "neutral";

interface LicenseRow {
    name: string;
    status: { label: string; variant: TagVariant };
    /** Calendar date, `YYYY-MM-DD`; `null` when it does not end or is not in the plan. */
    until: string | null;
}

type InPlanService = LicenseService & { status: Exclude<LicenseService["status"], "not_in_plan"> };

const isInPlan = (service: LicenseService): service is InPlanService => service.status !== "not_in_plan";

const SERVICE_STATUS: Record<InPlanService["status"], LicenseRow["status"]> = {
    active: { label: "Active", variant: "success" },
    expired: { label: "Expired", variant: "error" },
};

/** The VM license's status: valid, any other state FortiManager names, or unknown — never a warning for what nobody read. */
function vmLicenseStatus(status: string | null): LicenseRow["status"] {
    if (status === "valid") return { label: "Valid", variant: "success" };
    if (status == null) return { label: EMPTY, variant: "neutral" };
    return { label: status, variant: "warning" };
}

/**
 * One row per thing the customer pays for, in a table (CTX-1208, design review): the
 * VM license, support, each security service of the plan and whether the firewall gets
 * its security updates. A service outside the plan has no row (CTX-1481, review of
 * 26-oct): the card lists what covers the firewall, not what could. An expired one
 * stays — it was in the plan and no longer covers, and Observations says so too.
 */
export function licenseRows(licenses: LicensesSection): LicenseRow[] {
    return [
        {
            name: "VM license",
            status: vmLicenseStatus(licenses.vm.status),
            until: licenses.vm.expires_on,
        },
        {
            name: "Support",
            status: { label: licenses.support.level ?? EMPTY, variant: "neutral" },
            until: licenses.support.expires_on,
        },
        ...licenses.services.filter(isInPlan).map((service) => ({
            name: serviceLabel(service),
            status: SERVICE_STATUS[service.status] ?? { label: service.status, variant: "neutral" as const },
            until: service.status === "active" ? service.expires_on : null,
        })),
        {
            name: "Security updates",
            status:
                licenses.security_updates === "connected"
                    ? { label: "Connected", variant: "success" }
                    : { label: "Not connected", variant: "warning" },
            until: null,
        },
    ];
}

const COLUMNS: GenericTableColumn<LicenseRow>[] = [
    { header: "License", wrapMode: "wrap", renderCell: (row) => row.name },
    {
        header: "Status",
        width: "120px",
        renderCell: (row) => <GenericTag label={row.status.label} variant={row.status.variant} size="small" />,
    },
    {
        header: "Until",
        width: "120px",
        renderCell: (row) => (row.until ? formatCalendarDate(row.until) : <span className="text-semantic-text-secondary">{EMPTY}</span>),
    },
];

export default function LicensesCard({ licenses }: { licenses: LicensesSection | null | undefined }) {
    return (
        <OverviewCard title="Licenses & protection" section={licenses} fill>
            {(section) => (
                // Beside Policies, the table fills what the card has left, so both end at the
                // same line (CTX-1272). Laid over that space rather than in it, so its rows
                // never make the row taller than Policies; stacked, it shows them all.
                <div className="relative flex-1 min-h-0 min-[1440px]:min-h-[200px]">
                    <div className="min-[1440px]:absolute min-[1440px]:inset-0">
                        <GenericTable
                            maxTableBodyHeight="100%"
                            isFooterVisible={false}
                            columns={COLUMNS}
                            data={licenseRows(section)}
                            rowKey={(row) => row.name}
                            width="100%"
                            initialRowsPerPage={50}
                            tableClassName="bg-semantic-bg-primary"
                        />
                    </div>
                </div>
            )}
        </OverviewCard>
    );
}
